import { FixTemplateTargetFilterField } from './types';

import {
  TLAICodeScanItemPriority,
  TLAIMessageType,
  TLAIRemedyObjectStatus,
  TLAIRemedyScanStatus,
  TLAIRemedyStatus,
  TLAIRemedyV3FindingStatus,
} from '@/types/api';

export const FixTemplateTargetFilterFieldLabels = {
  [FixTemplateTargetFilterField.NOTE]: 'Note',
  [FixTemplateTargetFilterField.CHECK_TITLE]: 'Check Title',
  [FixTemplateTargetFilterField.CHECK_MESSAGE]: 'Check Message',
  [FixTemplateTargetFilterField.REFERENCE_OBJECT]: 'Reference Object',
  [FixTemplateTargetFilterField.CUSTOM_FIELD]: 'Custom Field',
} as const;

export const IssuePriorityLabels = {
  [TLAICodeScanItemPriority.LOW]: 'Scan Issues',
  [TLAICodeScanItemPriority.MEDIUM]: 'Medium Scan Issues',
  [TLAICodeScanItemPriority.HIGH]: 'Critical Scan Issues',
} as const;

export const SyntaxIssueTypeLabels = {
  [TLAIMessageType.ERROR]: 'Syntax Errors',
  [TLAIMessageType.WARNING]: 'Syntax Warnings',
  [TLAIMessageType.INFO]: 'Info',
  [TLAIMessageType.UNKNOWN]: 'Other',
} as const;

export const RemedyStatusBadgeLabels = {
  [TLAIRemedyStatus.CREATED]: 'Created',
  [TLAIRemedyStatus.INITIAL_SCAN_IN_PROGRESS]: 'Scanning',
  [TLAIRemedyStatus.INITIAL_SCAN_COMPLETED]: 'Scan Complete',
  [TLAIRemedyStatus.INITIAL_SCAN_FAILED]: 'Scan Failed',
  [TLAIRemedyStatus.SETUP_COMPLETED]: 'Ready',
  [TLAIRemedyStatus.SETUP_IN_PROGRESS]: 'Preparing',
  [TLAIRemedyStatus.SETUP_FAILED]: 'Setup Failed',
} as const;

export const ScanStatusBadgeLabels = {
  [TLAIRemedyScanStatus.CREATED]: 'Created',
  [TLAIRemedyScanStatus.IN_PROGRESS]: 'In Progress',
  [TLAIRemedyScanStatus.COMPLETED]: 'Completed',
  [TLAIRemedyScanStatus.CANCELED]: 'Canceled',
  [TLAIRemedyScanStatus.FAILED]: 'Failed',
} as const;

export const ObjectStatusBadgeLabels = {
  [TLAIRemedyObjectStatus.NOT_STARTED]: 'Not Started',
  [TLAIRemedyObjectStatus.IN_PROGRESS]: 'In Progress',
  [TLAIRemedyObjectStatus.FLAGGED_FOR_FUNCTIONAL]: 'Needs Functional Review',
  [TLAIRemedyObjectStatus.READY_FOR_REVIEW]: 'Awaiting Review',
  [TLAIRemedyObjectStatus.COMPLETED]: 'Completed',
} as const;

export const FindingStatusBadgeLabels = {
  [TLAIRemedyV3FindingStatus.IN_PROGRESS]: 'In Progress',
  [TLAIRemedyV3FindingStatus.FLAGGED_FOR_FUNCTIONAL]: 'Needs Functional Review',
  [TLAIRemedyV3FindingStatus.BUSINESS_APPROVED_TO_IGNORE]: 'Accepted As-Is',
  [TLAIRemedyV3FindingStatus.ON_HOLD]: 'On Hold',
  [TLAIRemedyV3FindingStatus.MANUAL_CHANGE_NEEDED]: 'Manual Change Needed',
} as const;

/**
 * Buckets findings by how much attention they still need, so issue lists surface
 * actionable work first. Statuses sharing a rank stay ordered by line number.
 * `satisfies` makes a newly added status a compile error rather than a silent
 * fall-through to the end of the list.
 */
export const FindingStatusSortRank = {
  [TLAIRemedyV3FindingStatus.IN_PROGRESS]: 0,
  [TLAIRemedyV3FindingStatus.MANUAL_CHANGE_NEEDED]: 1,
  [TLAIRemedyV3FindingStatus.ON_HOLD]: 1,
  [TLAIRemedyV3FindingStatus.FLAGGED_FOR_FUNCTIONAL]: 2,
  [TLAIRemedyV3FindingStatus.BUSINESS_APPROVED_TO_IGNORE]: 2,
} as const satisfies Record<TLAIRemedyV3FindingStatus, number>;

/**
 * Fixed correlation id standing in for "the tenant-wide Code Remediation scope".
 *
 * A program saved with no correlation_id is scoped by the backend to the creating
 * *user*, so global AI Instructions were never actually shared. The backend requires
 * the id to be a UUID, hence a sentinel rather than a readable string. Tenant isolation
 * comes from the X-Tenant-ID header, so one value is safe across tenants. Never change
 * it — doing so orphans every saved instruction file.
 */
export const CODE_REMEDIATION_GLOBAL_CORRELATION_ID = '9f3017e7-cb3c-4b3b-bf9c-22662792ddb3';
